import React from 'react';
import { Platform, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { formatWeekdayDay, formatWeekdayShort } from '../../../../utils/dateLocale';

const WEB = Platform.OS === 'web';
const THUMB = 56;
/** One pulse; the wave's offset per row is a fraction of it, so the rows never sync up. */
const PULSE_MS = 1300;
const WAVE_STEP_MS = 140;
const RISE_MS = 320;

interface Props {
  dayIso: string;
  /** Draws the day inside the card, like the compact MealRow it turns into. */
  withDayTag: boolean;
  /** Its place in the wave, top to bottom. */
  index: number;
  animate: boolean;
}

/**
 * An evening « Planifier les 7 jours suivants » is choosing a dinner for (#851).
 *
 * The shape of the compact MealRow it becomes — day, photo, title, chips, the
 * two action buttons — so nothing moves when the dinner takes its place.
 *
 * The pulse is a CSS animation, not `Animated`: the generator holds the main
 * thread for seconds on an iPad, and a JS-driven pulse would freeze for exactly
 * as long as it is there to cover. An opacity keyframe runs on the compositor.
 */
export function PendingDinnerRow({ dayIso, withDayTag, index, animate }: Props) {
  const { t, i18n } = useTranslation();
  const pulse = animate && WEB
    ? [webStyles.pulse, { animationDelay: `${index * WAVE_STEP_MS}ms` } as ViewStyle]
    : styles.still;

  return (
    <View
      style={styles.wrap}
      testID={`pending-dinner-${dayIso}`}
      accessible
      accessibilityLabel={t('thisWeek.extend.pendingDay', { day: formatWeekdayDay(dayIso, i18n.language) })}
    >
      <View style={styles.card}>
        {withDayTag && (
          <View style={styles.dayTag}>
            <Text style={styles.dayTagShort}>{formatWeekdayShort(dayIso, i18n.language).toUpperCase()}</Text>
            <Text style={styles.dayTagNum}>{Number(dayIso.slice(8, 10))}</Text>
          </View>
        )}
        <View style={[styles.photo, pulse]} />
        <View style={[styles.body, pulse]}>
          <View style={[styles.bar, styles.titleBar, { width: `${58 + ((index * 17) % 30)}%` }]} />
          <View style={styles.chips}>
            <View style={[styles.bar, styles.chip]} />
            <View style={[styles.bar, styles.chipShort]} />
          </View>
        </View>
        <View style={[styles.actions, pulse]}>
          <View style={styles.actionDot} />
          <View style={styles.actionDot} />
        </View>
      </View>
    </View>
  );
}

/**
 * A dinner taking a placeholder's place: it fades up from a few pixels lower.
 * Always a View, so a row that has finished rising is never remounted.
 */
export function RiseIn({ rise, children }: { rise: boolean; children: React.ReactNode }) {
  return <View style={rise && WEB ? webStyles.rise : undefined}>{children}</View>;
}

/** How long one dinner takes to come in; the next starts `STAG_MS_MOBILE` after it. */
export const ARRIVAL_RISE_MS = RISE_MS;

// react-native-web compiles `animationKeyframes` into a CSS @keyframes rule;
// React Native's types do not know the web-only animation properties.
const webStyles = StyleSheet.create({
  pulse: {
    animationKeyframes: [{ '0%': { opacity: 1 }, '50%': { opacity: 0.45 }, '100%': { opacity: 1 } }],
    animationDuration: `${PULSE_MS}ms`,
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
    animationFillMode: 'both',
  },
  rise: {
    animationKeyframes: [{
      '0%': { opacity: 0, transform: [{ translateY: 10 }] },
      '100%': { opacity: 1, transform: [{ translateY: 0 }] },
    }],
    animationDuration: `${RISE_MS}ms`,
    animationTimingFunction: 'cubic-bezier(0.2, 0.7, 0.3, 1)',
    animationFillMode: 'both',
  },
} as unknown as Record<'pulse' | 'rise', ViewStyle>);

const styles = StyleSheet.create({
  // MealRow's wrap: 4px above, its bare label row, a 4px gap, the card, 4px below.
  wrap: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.10)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  still: {
    opacity: 0.75,
  },
  dayTag: {
    width: 40,
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayTagShort: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    letterSpacing: 0.6,
    color: Paprika.textMuted,
  },
  dayTagNum: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 21,
    lineHeight: 24,
    color: Paprika.textPrimary,
  },
  photo: {
    width: THUMB,
    height: THUMB,
    borderRadius: 12,
    backgroundColor: Paprika.bgSunk,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 9,
  },
  bar: {
    borderRadius: 999,
    backgroundColor: Paprika.surfaceLow,
  },
  titleBar: {
    height: 13,
  },
  chips: {
    flexDirection: 'row',
    gap: 6,
  },
  chip: {
    width: 52,
    height: 20,
  },
  chipShort: {
    width: 34,
    height: 20,
  },
  actions: {
    gap: 6,
  },
  actionDot: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: Paprika.surfaceLow,
  },
});
